import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { DataTable, Pager, StatusDot, TableToolbar } from '@/components/DataTable';
import { NewPostModal } from '@/components/NewPostModal';
import { TopicsModal } from '@/components/TopicsModal';
import { saveMessage, useDebounced } from '@/lib/apps';
import { useHeaderActions } from '@/lib/editor-bar';
import { POSTS_PER_PAGE, listTitle, postDate, statusOf, useAdminPosts } from '@/lib/posts';
import { topicName } from '@/lib/topics';

const GRID = 'minmax(0, 2.4fr) 0.9fr 0.7fr 0.9fr 88px';

export default function BlogPage() {
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);

  /*
   * The box updates as fast as somebody types; the query waits for them to
   * stop. Without it every character is a request and the rate limit is five in
   * the window — the apps list learned this by showing "Too many requests" for
   * a six-letter search that was perfectly valid.
   */
  const search = useDebounced(filter);

  /*
   * Back to page 1 whenever the search changes. Page 3 is past the end of most
   * searches, and a table answering a new search with "no posts" because it is
   * still on page 3 says the search failed when it did not.
   *
   * ⚠️ Adjusted **during render** rather than in an effect — see the long note
   * in `AppsPage`. An effect runs after the render commits, so the query would
   * already have gone out with the new search and the old page: a request
   * nobody wanted, against the rate limit the debounce beside it protects.
   */
  const [searchedFor, setSearchedFor] = useState(search);
  if (searchedFor !== search) {
    setSearchedFor(search);
    setPage(1);
  }

  const posts = useAdminPosts({ search, page });

  // And back onto a page that exists when the blog shrinks under one — see the
  // long note in `AppsPage`. Deleting the last post on the final page is the
  // way somebody meets this.
  const pages = Math.max(1, Math.ceil((posts.data?.total ?? 0) / POSTS_PER_PAGE));
  if (posts.data && page > pages) setPage(pages);

  /*
   * Published in the header, because v2 puts these two where the page title
   * used to be. Memoised because the header compares what it is handed —
   * a fresh array every render would re-run the effect that publishes it.
   */
  const actions = useMemo(
    () => [
      { label: 'Topics', onClick: () => setTopicsOpen(true), kind: 'outline' as const },
      {
        label: 'New post',
        onClick: () => setNewOpen(true),
        kind: 'accent' as const,
        icon: 'plus' as const,
      },
    ],
    [],
  );
  useHeaderActions(actions);

  const rows = posts.data?.items ?? [];
  const total = posts.data?.total ?? 0;

  /*
   * ⚠️ **The filtering moved to the server, and had to.** It used to run over
   * the fetched array here, which was right while the whole table was fetched.
   * One page at a time and that same code searches only the page on screen — so
   * a post two pages down comes back "no posts match", which is a wrong answer
   * given confidently and worse than the slowness the paging was for.
   *
   * The "not yet live" count moved with it, for the same reason. It was worked
   * out from the rows in hand, which was right while every row was in hand;
   * left that way it would silently start meaning "on this page" — the same
   * sentence, in the same place, quietly answering a narrower question.
   */
  const meta = posts.data
    ? `${total} ${total === 1 ? 'post' : 'posts'} · ${posts.data.notYetLive} not yet live`
    : '';

  return (
    <>
      <section className="px-[18px] pt-3.5">
        <TableToolbar
          label="Filter posts"
          value={filter}
          onChange={setFilter}
          placeholder="Filter posts…"
          meta={meta}
        />

        {posts.isPending ? (
          <p className="text-[12.5px]" style={{ color: 'var(--muted)' }}>
            Loading…
          </p>
        ) : null}

        {posts.isError ? (
          <p className="text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {saveMessage(posts.error)}
          </p>
        ) : null}

        {!posts.isPending && !posts.isError ? (
          <DataTable
            gridTemplate={GRID}
            columns={['Title', 'Topic', 'Author', 'Date', 'Status']}
          >
            {rows.map((post) => {
              const title = listTitle(post.title);
              return (
                /*
                 * The whole row is the link, not just the title: the row is
                 * what somebody aims at, and a two-word target inside it is a
                 * click that mostly misses.
                 */
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="row-link grid items-center gap-3 border-b px-3.5 py-2"
                  style={{
                    gridTemplateColumns: GRID,
                    borderColor: 'var(--line)',
                    color: 'inherit',
                  }}
                >
                  <span className="flex min-w-0 items-baseline gap-2.5">
                    {/*
                      Marked when the title is not in English, for the same
                      reason the website marks a block that fell back: it is one
                      language's text sitting in a screen written in another.
                    */}
                    <span
                      className="truncate text-[13px] font-medium"
                      lang={title.locale === 'en' ? undefined : title.locale}
                    >
                      {title.text}
                    </span>
                    <span
                      className="truncate font-mono text-[10.5px]"
                      style={{ color: 'var(--dim)' }}
                    >
                      {post.slug}
                    </span>
                    {/*
                      Which post is at the top of the blog, on the one screen
                      that shows them all. Only one post can be, and without
                      this the only way to find out which is to open them one at
                      a time — so the rule the checkbox enforces would be
                      invisible from the list it applies to.
                    */}
                    {post.featured ? (
                      /*
                        And it says which kind. A draft can be marked and the
                        blog does not show it, so a plain "Featured" beside the
                        title reads as "this is on the front of the blog" — with
                        the Draft dot four columns away where nobody joins the
                        two up. The editor makes the same distinction in words;
                        this is the same sentence with less room.
                      */
                      <span
                        className="shrink-0 rounded-full px-1.5 py-px text-[9.5px] font-medium"
                        style={{
                          background: 'var(--accent-soft)',
                          color: 'var(--accent)',
                          opacity: statusOf(post.publishedAt) === 'Published' ? 1 : 0.55,
                        }}
                      >
                        {statusOf(post.publishedAt) === 'Published' ? 'Featured' : 'Featured when live'}
                      </span>
                    ) : null}
                  </span>
                  {/*
                    Em dash rather than a blank for a post nobody has filed yet:
                    the column is there either way, and an empty cell reads as a
                    row that failed to load.
                  */}
                  <span className="truncate text-xs" style={{ color: 'var(--muted)' }}>
                    {post.topic ? topicName(post.topic.name) : '—'}
                  </span>
                  <span className="truncate text-xs" style={{ color: 'var(--muted)' }}>
                    {post.author || '—'}
                  </span>
                  <span className="font-mono text-[10.5px]" style={{ color: 'var(--dim)' }}>
                    {postDate(post.publishedAt)}
                  </span>
                  <StatusDot status={statusOf(post.publishedAt)} />
                </Link>
              );
            })}
            {/*
              Two empty states, because they call for two different actions:
              nothing exists yet, or nothing matches what was typed. One message
              for both sends somebody to import posts they already have.

              Decided on the *debounced* search rather than the box, because
              that is the question the server answered — reading the box would
              name a search that has not been run yet.
            */}
            {total === 0 ? (
              <p className="m-0 px-3.5 py-4 text-[12.5px]" style={{ color: 'var(--muted)' }}>
                {search.trim() ? (
                  <>No post matches “{search.trim()}”.</>
                ) : (
                  <>
                    No posts yet. Load the ones from the design file with{' '}
                    <span className="font-mono">yarn api:post-import</span>.
                  </>
                )}
              </p>
            ) : null}
          </DataTable>
        ) : null}
        <Pager page={page} perPage={POSTS_PER_PAGE} total={total} onPage={setPage} noun="post" />
      </section>
      <div className="h-6" />
      {topicsOpen ? <TopicsModal onClose={() => setTopicsOpen(false)} /> : null}
      {newOpen ? <NewPostModal onClose={() => setNewOpen(false)} /> : null}
    </>
  );
}
